import moment from 'moment-timezone';
import { PlanDefinitions } from '.';
import { ProductPricing } from '../common/types';
import { isFreePrice } from '../common/utils';
import { LocationSubscription } from '../subscriptions';
import { Plan, PurchasedPlan, Plans, SubscriptionPlan } from './types';

export const getLocationPrice = (
	purchasedPlan: PurchasedPlan,
	locationId?: string,
): ProductPricing | null => {
	const subscription = !!locationId ? purchasedPlan?.subscription?.locations?.[locationId] : null;
	return !!subscription
		? getSubscriptionPrice(purchasedPlan, subscription)
		: getPurchasedPlanPrice(purchasedPlan);
};

export const getSubscriptionPrice = (
	purchasedPlan: PurchasedPlan,
	subscription: LocationSubscription,
): ProductPricing | null => {
	switch (subscription.pricing.type) {
		case 'custom':
			return subscription.pricing.price;
		case 'free':
			return null;
		default:
			return getPurchasedPlanPrice(purchasedPlan);
	}
};

export const getPurchasedPlanPrice = (purchasedPlan: PurchasedPlan): ProductPricing | null => {
	switch (purchasedPlan.pricing.type) {
		case 'default':
			const plan = PlanDefinitions[purchasedPlan.plan];

			const pricing = {
				fixed: plan.pricing?.fixed?.amount ?? null,
				commission: plan.pricing?.commission?.percentage ?? null,
			};

			return isFreePrice(pricing) ? null : pricing;
		case 'custom': {
			const pricing = purchasedPlan.pricing.price;

			return isFreePrice(pricing) ? null : pricing;
		}
		case 'free': {
			return null;
		}
	}
};

export const getDefaultPurchasedPlan = (
	plan: Plan,
	startDate: moment.Moment = moment.utc(),
): PurchasedPlan => {
	return {
		plan,
		startDate: startDate.toISOString(),
		pricing: {
			type: 'default',
		},
		blockSlots: {
			type: 'default',
		},
	};
};

export const isSubscriptionPlan = (plan: Plan): plan is SubscriptionPlan => {
	return ['BASIC', 'GROW', 'ADVANCED'].includes(plan);
};

export const isLitePlan = (plan?: Plan): boolean => {
	return plan === Plans.LITE;
};

export const isCorePlan = (plan?: Plan): boolean => isOldCorePlan(plan);

//TODO Remove this when we remove references to the old plans from the codebase
export const isOldCorePlan = (plan?: Plan): boolean =>
	plan === Plans.CORE_COMMISSION ||
	plan === Plans.CORE_FIXED ||
	plan === Plans.CORE_DEMO ||
	plan === Plans.ENTERPRISE ||
	plan === Plans.CORE_INTERNAL_ONLY;

export const isEnterprisePlan = (plan?: Plan): boolean => plan === Plans.ENTERPRISE;
export const isBasicPlan = (plan?: Plan): boolean =>
	plan === Plans.BASIC || plan === Plans.GROW || plan === Plans.ADVANCED;
export const isGrowPlan = (plan?: Plan): boolean => plan === Plans.GROW || plan === Plans.ADVANCED;
export const isAdvancedPlan = (plan?: Plan): boolean => plan === Plans.ADVANCED;

export const planHasPrice = (plan: PurchasedPlan): boolean => {
	const price = getPurchasedPlanPrice(plan);

	return !isFreePrice(price);
};

export const isPlanDowngrade = (plan: Plan, currentPlan: Plan | undefined) => {
	if (!currentPlan || isCorePlan(plan)) return false;

	const orderedPlans = ['LITE', 'BASIC', 'GROW', 'ADVANCED'];

	return orderedPlans.indexOf(plan) < orderedPlans.indexOf(currentPlan);
};

export const getPreviousPlanName = (plan: Plan) => {
	switch (plan) {
		case 'BASIC':
			return PlanDefinitions.LITE.name;
		case 'GROW':
			return PlanDefinitions.BASIC.name;
		case 'ADVANCED':
			return PlanDefinitions.GROW.name;
		default:
			return '';
	}
};
